import React, { useMemo, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import {
  estimatedPrintInchesAtDpi,
  projectFactorOutput,
  type ImageResolution,
} from '../services/resolution';

export type UpscaleCapabilities = {
  enabled: boolean;
  factors: number[];
  maxOutputPixels?: number | null;
  creditCost: number;
  creditsRequired?: boolean;
};

type Props = {
  open: boolean;
  resolution: ImageResolution;
  capabilities: UpscaleCapabilities;
  /** From product config — authoritative CREDIT_MODE. */
  creditMode?: string;
  stage?: string;
  creditsEnforced?: boolean;
  restorationsCurrentlyFree?: boolean;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (factor: number) => void;
};

export const UpscaleDialog: React.FC<Props> = ({
  open,
  resolution,
  capabilities,
  creditMode = 'disabled',
  stage = 'alpha',
  creditsEnforced = false,
  restorationsCurrentlyFree = true,
  busy = false,
  error = null,
  onClose,
  onConfirm,
}) => {
  const factors = capabilities.factors?.length ? capabilities.factors : [2, 3, 4];
  const [selected, setSelected] = useState<number>(() => {
    const firstOk = factors.find((f) =>
      projectFactorOutput(resolution.width, resolution.height, f, capabilities.maxOutputPixels)
        .withinLimit
    );
    return firstOk || factors[0];
  });

  const options = useMemo(
    () =>
      factors.map((factor) => {
        const proj = projectFactorOutput(
          resolution.width,
          resolution.height,
          factor,
          capabilities.maxOutputPixels
        );
        const print = estimatedPrintInchesAtDpi(proj.outputWidth, proj.outputHeight, 300);
        return { factor, ...proj, print };
      }),
    [factors, resolution, capabilities.maxOutputPixels]
  );

  const selectedOpt = options.find((o) => o.factor === selected);
  // Never gate the button on credit balance — only provider limits / busy state
  const canSubmit = Boolean(selectedOpt?.withinLimit) && !busy;

  if (!open) return null;

  // Same free-mode philosophy as restoration: only enforced mode shows a credit cost
  const enforced =
    creditsEnforced === true ||
    creditMode === 'enforced' ||
    capabilities.creditsRequired === true;
  const freeDuringAlpha =
    !enforced &&
    (stage === 'alpha' ||
      creditMode === 'disabled' ||
      restorationsCurrentlyFree ||
      creditMode === 'metered');

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
        aria-label="Close upscale dialog"
        onClick={() => !busy && onClose()}
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Upscale restoration</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Increases resolution for print. Your restoration master stays unchanged.
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Current</div>
            <div className="font-semibold text-slate-800">
              {resolution.width.toLocaleString()} × {resolution.height.toLocaleString()}
            </div>
            <div className="text-slate-600">{resolution.megapixels} MP</div>
          </div>

          <div className="space-y-2">
            {options.map((opt) => {
              const disabled = !opt.withinLimit || busy;
              const active = selected === opt.factor;
              return (
                <button
                  key={opt.factor}
                  type="button"
                  disabled={disabled && !opt.withinLimit}
                  onClick={() => opt.withinLimit && setSelected(opt.factor)}
                  className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${
                    !opt.withinLimit
                      ? 'border-slate-100 bg-slate-50 opacity-70 cursor-not-allowed'
                      : active
                        ? 'border-teal-500 bg-teal-50/80 ring-1 ring-teal-500'
                        : 'border-slate-200 hover:border-teal-300 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-slate-900">{opt.factor}×</span>
                    {!opt.withinLimit ? (
                      <span className="text-[10px] font-medium text-rose-700">Unavailable</span>
                    ) : null}
                  </div>
                  {opt.withinLimit ? (
                    <>
                      <div className="text-xs text-slate-700 mt-0.5">
                        {opt.outputWidth.toLocaleString()} × {opt.outputHeight.toLocaleString()}
                        <span className="text-slate-500"> · {opt.megapixels} MP</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Estimated print size @ 300 DPI: {opt.print.widthIn} × {opt.print.heightIn}{' '}
                        in
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] text-slate-600 mt-0.5">
                      Projected output: {opt.outputWidth.toLocaleString()} ×{' '}
                      {opt.outputHeight.toLocaleString()}. Exceeds current upscale limit.
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {enforced ? (
            <p className="text-[11px] text-slate-600">
              Cost: {capabilities.creditCost} Kinvera credit
              {capabilities.creditCost === 1 ? '' : 's'}
            </p>
          ) : freeDuringAlpha ? (
            <p className="text-[11px] text-teal-800 font-medium">
              Free during Kinvera Alpha
            </p>
          ) : (
            <p className="text-[11px] text-slate-500">
              Upscaling is currently free. Your restoration is unchanged if it fails.
            </p>
          )}

          {error ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              {error}
            </div>
          ) : null}

          {busy ? (
            <div className="flex items-center gap-2 text-xs font-medium text-teal-800">
              <Loader2 className="w-4 h-4 animate-spin" />
              Upscaling {selected}×...
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50/80">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="min-h-10 px-3 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-200/70 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onConfirm(selected)}
            className="min-h-10 px-4 rounded-lg bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white text-xs font-bold"
          >
            Upscale {selected}×
          </button>
        </div>
      </div>
    </div>
  );
};
